import { navigationAssistantFallbackPaths } from '../constants/navigationAssistantCatalog';

const DEFAULT_LIMIT = 3;

const groupLabelKeys = {
  public: 'nav.general',
  approval: 'nav.approval',
  account: 'nav.account',
  content: 'nav.content',
  training: 'nav.training',
  admin: 'nav.admin',
};

const lexicalStopWords = new Set([
  'a', 'an', 'are', 'can', 'do', 'find', 'for', 'go', 'how', 'i', 'is', 'me', 'my', 'open', 'page', 'please', 'show', 'the', 'to', 'where', 'you',
  'de', 'des', 'du', 'het', 'ik', 'je', 'la', 'le', 'les', 'mein', 'meine', 'mon', 'ma', 'mijn', 'ou', 'waar', 'wie', 'wo',
]);

const profileTokens = new Set(['profile', 'profiles', 'profil', 'profils', 'profiel', 'profielen']);
const accountHintTokens = new Set([
  'account', 'bearbeiten', 'bewerken', 'compte', 'edit', 'einstellungen', 'instellingen', 'konto', 'langue', 'language', 'motdepasse', 'parametres', 'password', 'praferenzen', 'preferences', 'profileinstellungen', 'settings', 'sprache', 'taal', 'wachtwoord',
]);
const trainingHintTokens = new Set([
  'formation', 'simulation', 'simulations', 'simulatie', 'simulaties', 'simulator', 'trainer', 'traineren', 'training', 'trainings', 'trainingsprofile', 'trainingsprofielen',
]);

const conflictingIntents = {
  'account-profile': ['training-profile'],
  'training-profile': ['account-profile'],
};

const pickLocalizedValue = (localizedValue, language, fallbackValue) => {
  if (!localizedValue || typeof localizedValue !== 'object') {
    return fallbackValue;
  }

  const baseLanguage = (language || 'en').split('-')[0];
  return localizedValue[language] ?? localizedValue[baseLanguage] ?? localizedValue.en ?? fallbackValue;
};

/**
 * Normalizes user-entered text so multilingual keyword matching is more forgiving.
 * @param {string} value - Raw user-entered or translated text
 * @returns {string} Search-safe text
 */
export const normalizeNavigationText = (value = '') => (
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

/**
 * Splits normalized text into tokens while ignoring single-character noise.
 * @param {string} value - Raw text
 * @returns {string[]} Search tokens
 */
export const tokenizeNavigationText = (value = '') => {
  const normalized = normalizeNavigationText(value);
  if (!normalized) {
    return [];
  }

  return normalized
    .split(' ')
    .filter((token) => token.length > 1);
};

const filterLexicalTokens = (tokens) => (
  tokens.filter((token) => !lexicalStopWords.has(token))
);

const inferQuerySignals = (queryText, rawTokens) => {
  const tokenSet = new Set(rawTokens);
  const hasProfileToken = rawTokens.some((token) => profileTokens.has(token));
  const hasAccountHint = rawTokens.some((token) => accountHintTokens.has(token));
  const hasTrainingHint = rawTokens.some((token) => trainingHintTokens.has(token));
  const hasMyProfilePhrase = queryText.includes('my profile') || queryText.includes('mon profil') || queryText.includes('mein profil') || queryText.includes('mijn profiel');
  const hasTrainingProfilePhrase = queryText.includes('training profile')
    || queryText.includes('training profiles')
    || queryText.includes('simulation profile')
    || queryText.includes('simulation profiles')
    || queryText.includes('trainingsprofil')
    || queryText.includes('trainingsprofile')
    || queryText.includes('trainingsprofiel')
    || queryText.includes('trainingsprofielen')
    || queryText.includes('profil de formation')
    || queryText.includes('profils de formation');

  const intents = new Set();

  if (hasProfileToken && (hasMyProfilePhrase || hasAccountHint)) {
    intents.add('account-profile');
  }

  if ((hasProfileToken && hasTrainingHint) || hasTrainingProfilePhrase) {
    intents.add('training-profile');
  }

  return {
    intents,
    hasProfileToken,
    tokenSet,
  };
};

const readKeywords = (entry, t, keywordsKey, language) => {
  if (entry.keywords) {
    return pickLocalizedValue(entry.keywords, language, []);
  }

  const translated = t(keywordsKey, { returnObjects: true, defaultValue: [] });
  return Array.isArray(translated) ? translated : [];
};

const readRouteTexts = (entry, route, t, language) => {
  const label = pickLocalizedValue(
    entry.titles,
    language,
    t(entry.titleKey, { defaultValue: route?.name || entry.id }),
  );
  const description = pickLocalizedValue(
    entry.descriptions,
    language,
    t(entry.descriptionKey, {
      defaultValue: route?.description || '',
    }),
  );
  const keywords = readKeywords(entry, t, entry.keywordsKey, language);

  return {
    label,
    description,
    keywords,
  };
};

const getAccessState = (route, isAuthenticated, hasAnyRole) => {
  if (!route) {
    return 'missing';
  }

  if (route.requiresAuth && !isAuthenticated) {
    return 'login';
  }

  if (route.roles && route.roles.length > 0 && !hasAnyRole(route.roles)) {
    return isAuthenticated ? 'role' : 'login';
  }

  return 'allowed';
};

const scoreNavigationEntry = (queryText, queryTokens, label, description, keywords, priority, entry, querySignals) => {
  if (!queryText) {
    return priority;
  }

  const normalizedLabel = normalizeNavigationText(label);
  const normalizedDescription = normalizeNavigationText(description);
  const normalizedKeywords = keywords.map((keyword) => normalizeNavigationText(keyword));
  let score = 0;

  if (normalizedLabel === queryText) {
    score += 120;
  } else if (normalizedLabel.includes(queryText)) {
    score += 60;
  }

  if (normalizedDescription.includes(queryText)) {
    score += 32;
  }

  normalizedKeywords.forEach((keyword) => {
    if (keyword === queryText) {
      score += 70;
      return;
    }

    if (keyword.includes(queryText) || queryText.includes(keyword)) {
      score += 28;
    }
  });

  queryTokens.forEach((token) => {
    if (normalizedLabel.includes(token)) {
      score += 14;
    }

    if (normalizedDescription.includes(token)) {
      score += 6;
    }

    if (normalizedKeywords.some((keyword) => keyword.includes(token))) {
      score += 10;
    }
  });

  if (entry.intents && entry.intents.length > 0 && querySignals.intents.size > 0) {
    querySignals.intents.forEach((intent) => {
      if (entry.intents.includes(intent)) {
        score += 120;
      }

      const conflicts = conflictingIntents[intent] || [];
      if (entry.intents.some((entryIntent) => conflicts.includes(entryIntent))) {
        score -= 90;
      }
    });
  }

  return score > 0 ? Math.max(score + priority, 0) : 0;
};

const buildSuggestion = (entry, route, texts, t, access) => ({
  id: entry.id,
  path: entry.path,
  label: texts.label,
  description: texts.description,
  groupLabel: route?.menuGroup ? t(groupLabelKeys[route.menuGroup], { defaultValue: '' }) : '',
  access,
});

const getFallbackSuggestions = ({ catalog, routeDefinitions, isAuthenticated, hasAnyRole, t, language, limit }) => {
  const routeByPath = new Map(routeDefinitions.map((route) => [route.path, route]));

  return navigationAssistantFallbackPaths
    .map((path) => catalog.find((entry) => entry.path === path))
    .filter(Boolean)
    .map((entry) => {
      const route = routeByPath.get(entry.path);
      const access = getAccessState(route, isAuthenticated, hasAnyRole);

      if (access !== 'allowed') {
        return null;
      }

      const texts = readRouteTexts(entry, route, t, language);
      return buildSuggestion(entry, route, texts, t, access);
    })
    .filter(Boolean)
    .slice(0, limit);
};

const shouldClarifyMatch = (rankedAccessible, queryText, rawTokens, querySignals) => {
  if (querySignals.intents.size > 0 || !querySignals.hasProfileToken) {
    return false;
  }

  const topTwo = rankedAccessible.slice(0, 2);
  if (topTwo.length < 2) {
    return false;
  }

  const ambiguityGroup = topTwo[0].entry.ambiguityGroup;
  if (!ambiguityGroup || !topTwo.every((result) => result.entry.ambiguityGroup === ambiguityGroup)) {
    return false;
  }

  const isShortGenericProfileQuery = rawTokens.length <= 2
    && rawTokens.every((token) => profileTokens.has(token) || lexicalStopWords.has(token));
  const scoreGap = topTwo[0].score - topTwo[1].score;

  return isShortGenericProfileQuery || queryText === 'profile' || queryText === 'profiles' || scoreGap <= 40;
};

/**
 * Finds the best navigation suggestions for a free-text user query.
 * @param {Object} options - Matching options
 * @param {Array} options.catalog - Curated assistant catalog entries
 * @param {Array} options.routeDefinitions - Route definitions from routes.js
 * @param {string} options.query - User query
 * @param {Function} options.t - i18n translation function
 * @param {boolean} options.isAuthenticated - Whether the user is signed in
 * @param {Function} options.hasAnyRole - Role matcher from auth context
 * @param {number} [options.limit=3] - Maximum number of suggestions to return
 * @returns {Object} Response payload for the assistant UI
 */
export const findNavigationAssistantResponse = ({
  catalog,
  routeDefinitions,
  query,
  t,
  language = 'en',
  isAuthenticated,
  hasAnyRole,
  limit = DEFAULT_LIMIT,
}) => {
  const routeByPath = new Map(routeDefinitions.map((route) => [route.path, route]));
  const queryText = normalizeNavigationText(query);
  const rawQueryTokens = tokenizeNavigationText(query);
  const queryTokens = filterLexicalTokens(rawQueryTokens);
  const querySignals = inferQuerySignals(queryText, rawQueryTokens);

  if (!queryText) {
    return {
      status: 'empty',
      message: t('assistant.responses.empty'),
      suggestions: getFallbackSuggestions({
        catalog,
        routeDefinitions,
        isAuthenticated,
        hasAnyRole,
        t,
        language,
        limit,
      }),
    };
  }

  const ranked = catalog
    .map((entry) => {
      const route = routeByPath.get(entry.path);
      if (!route) {
        return null;
      }

      const texts = readRouteTexts(entry, route, t, language);
      const score = scoreNavigationEntry(
        queryText,
        queryTokens,
        texts.label,
        texts.description,
        texts.keywords,
        entry.priority || 0,
        entry,
        querySignals,
      );
      const access = getAccessState(route, isAuthenticated, hasAnyRole);

      return {
        entry,
        score,
        suggestion: buildSuggestion(entry, route, texts, t, access),
      };
    })
    .filter((entry) => entry && entry.score > 0)
    .sort((left, right) => right.score - left.score);

  const accessibleRanked = ranked
    .filter((entry) => entry.suggestion.access === 'allowed')
    .slice(0, limit);

  const accessible = accessibleRanked.map((entry) => entry.suggestion);

  if (shouldClarifyMatch(accessibleRanked, queryText, rawQueryTokens, querySignals)) {
    return {
      status: 'clarify',
      message: t('assistant.responses.clarify'),
      suggestions: accessible,
    };
  }

  if (accessible.length > 0) {
    return {
      status: 'matched',
      message: t('assistant.responses.matches', { count: accessible.length }),
      suggestions: accessible,
    };
  }

  const restricted = ranked
    .filter((entry) => entry.suggestion.access === 'login' || entry.suggestion.access === 'role')
    .slice(0, limit)
    .map((entry) => entry.suggestion);

  if (restricted.length > 0) {
    const needsLogin = restricted.some((entry) => entry.access === 'login');

    if (needsLogin && !isAuthenticated) {
      return {
        status: 'restricted',
        message: t('assistant.responses.loginRequired'),
        suggestions: getFallbackSuggestions({
          catalog,
          routeDefinitions,
          isAuthenticated,
          hasAnyRole,
          t,
          language,
          limit,
        }),
      };
    }

    return {
      status: 'restricted',
      message: needsLogin
        ? t('assistant.responses.loginRequired')
        : t('assistant.responses.roleRestricted'),
      suggestions: restricted,
    };
  }

  return {
    status: 'fallback',
    message: t('assistant.responses.noMatch'),
    suggestions: getFallbackSuggestions({
      catalog,
      routeDefinitions,
      isAuthenticated,
      hasAnyRole,
      t,
      language,
      limit,
    }),
  };
};